import { DEFAULT_PALETTE } from '../../core/palette';
import type { ParamSchema, SchemaValues } from '../../core/params/types';

export const POINT_CLOUD_3D_PARAMS = [
  {
    kind: 'int',
    key: 'points',
    label: 'Punkte',
    min: 500,
    max: 500000,
    step: 100,
    scale: 'log',
    default: 60000,
    bulkRange: [8000, 180000],
    group: 'Wolke',
  },
  {
    kind: 'float',
    key: 'pointSize',
    label: 'Punktgrösse',
    min: 0.3,
    max: 14,
    step: 0.1,
    default: 2,
    unit: 'px',
    group: 'Wolke',
  },
  {
    kind: 'float',
    key: 'depthSpread',
    label: 'Tiefenstreuung',
    min: 0,
    max: 500,
    step: 1,
    default: 70,
    unit: 'px',
    bulkRange: [10, 220],
    group: 'Wolke',
  },
  {
    kind: 'float',
    key: 'explosion',
    label: 'Explosionsradius',
    min: 0,
    max: 900,
    step: 1,
    default: 0,
    unit: 'px',
    bulkRange: [0, 400],
    group: 'Wolke',
    tooltip: 'Streut die Punkte kugelförmig auseinander.',
  },
  {
    kind: 'float',
    key: 'noiseDisplacement',
    label: 'Rausch-Verschiebung',
    min: 0,
    max: 400,
    step: 1,
    default: 0,
    unit: 'px',
    group: 'Wolke',
    tooltip: 'Verzerrt die Wolke durch ein Rauschfeld in Z.',
  },
  {
    kind: 'float',
    key: 'noiseScale',
    label: 'Noise-Skala',
    min: 0.0004,
    max: 0.02,
    step: 0.0001,
    scale: 'log',
    default: 0.004,
    precision: 4,
    unit: '1/px',
    group: 'Wolke',
    showIf: (v) => (v.noiseDisplacement as number) > 0,
  },

  {
    kind: 'float',
    key: 'azimuth',
    label: 'Azimut',
    min: -180,
    max: 180,
    step: 1,
    default: 24,
    unit: '°',
    group: 'Kamera',
  },
  {
    kind: 'float',
    key: 'elevation',
    label: 'Höhenwinkel',
    min: -85,
    max: 85,
    step: 1,
    default: 14,
    unit: '°',
    group: 'Kamera',
  },
  {
    kind: 'float',
    key: 'distance',
    label: 'Distanz',
    min: 0.5,
    max: 6,
    step: 0.05,
    default: 1.9,
    unit: '×',
    group: 'Kamera',
  },
  {
    kind: 'float',
    key: 'fov',
    label: 'Blickwinkel',
    min: 8,
    max: 110,
    step: 1,
    default: 42,
    unit: '°',
    group: 'Kamera',
  },
  {
    kind: 'float',
    key: 'depthOfField',
    label: 'Tiefenunschärfe',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.35,
    group: 'Kamera',
    tooltip: 'Blendet weiter entfernte Punkte aus, statt sie unscharf zu rechnen.',
  },

  {
    kind: 'enum',
    key: 'colorMode',
    label: 'Farbmodus',
    default: 'depth',
    group: 'Farbe',
    options: [
      { value: 'solid', label: 'Einfarbig' },
      { value: 'depth', label: 'Nach Tiefe' },
      { value: 'position', label: 'Nach Position' },
      { value: 'random', label: 'Zufällig' },
    ],
  },
  {
    kind: 'palette',
    key: 'palette',
    label: 'Palette',
    default: { ...DEFAULT_PALETTE, paletteId: 'neon' },
    group: 'Farbe',
  },
  {
    kind: 'float',
    key: 'opacity',
    label: 'Deckkraft',
    min: 0.02,
    max: 1,
    step: 0.01,
    default: 0.75,
    group: 'Farbe',
  },
] as const satisfies ParamSchema;

export type PointCloud3dValues = SchemaValues<typeof POINT_CLOUD_3D_PARAMS>;
